import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Settings, RotateCcw, Trash2, LockKeyhole, Loader2, Edit } from 'lucide-react';
import { Type2PatternsDialog } from './Type2PatternsDialog';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';
import { EditRecentPatternDialog } from './EditRecentPatternDialog';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface Bot {
  id: string;
  name: string;
  type: string;
  status: string;
}

interface ConditionalSkipSetting {
  id: string;
  bot_id: string;
  enabled: boolean;
  forward_enabled: boolean;
  skip_type: string;
  forward_url: string | null;
  bots?: Bot;
}

interface BotState {
  id: string;
  bot_id: string;
  mode: 'GATE' | 'OPEN';
  recent_results?: string;
  manually_unlocked?: boolean;
  active_lock_pattern_id?: string | null;
  unlocked_at?: string | null;
}

interface PatternInfo {
  setting_id: string;
  pattern_mode: string;
  is_permanent: boolean;
  lock_pattern: string;
}

interface Type2BotCardProps {
  setting: ConditionalSkipSetting;
  state?: BotState;
  patternCount: number;
  patternInfo?: PatternInfo;
  onUpdate: (id: string, updates: any) => void;
  onReset: (botId: string) => void;
  onDelete: (id: string) => void;
  isUpdating: boolean;
  isResetting: boolean;
  isDeleting: boolean;
}

export const Type2BotCard: React.FC<Type2BotCardProps> = ({
  setting,
  state,
  patternCount,
  patternInfo, // ✅ Receive from props (batch fetched)
  onUpdate,
  onReset,
  onDelete,
  isUpdating,
  isResetting,
  isDeleting,
}) => {
  const [isPatternsOpen, setIsPatternsOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isEditPatternOpen, setIsEditPatternOpen] = useState(false);
  const queryClient = useQueryClient();

  // Unlock mutation
  const unlockMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('bot_current_condition_skip')
        .update({
          mode: 'OPEN',
          manually_unlocked: true,
          unlocked_by: user?.id,
          unlocked_at: new Date().toISOString(),
          active_lock_pattern_id: null,
          lock_matched_at: null
        })
        .eq('bot_id', setting.bot_id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['type2-bot-states'] });
      toast.success(`Bot ${setting.bots?.name} unlocked successfully`);
    },
    onError: (error: any) => {
      toast.error(`Failed to unlock: ${error.message}`);
    }
  });

  const getModeColor = () => {
    return state?.mode === 'OPEN' ? 'default' : 'secondary';
  };

  const isPermanentLocked = 
    state?.mode === 'GATE' && 
    patternInfo?.is_permanent === true;
  
  const isReverseMode = patternInfo?.pattern_mode === 'REVERSE';

  return (
    <>
      <Card className={cn(
        "hover:shadow-md transition-shadow",
        isPermanentLocked && "border-destructive shadow-destructive/20 shadow-lg"
      )}>
        <CardContent className="p-3 sm:p-6 sm:pt-6">
          <div className="space-y-3 sm:space-y-4">
            {/* Header */}
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-semibold text-sm sm:text-base truncate">{setting.bots?.name || 'Unknown Bot'}</h4>
                  
                  {/* Pattern Mode Badge */}
                  {isReverseMode && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                      🔄 REVERSE
                    </Badge>
                  )}
                  
                  {/* Tracking Only Badge */}
                  {setting.enabled && !setting.forward_enabled && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                      📋 Tracking Only
                    </Badge>
                  )}
                  
                  {isPermanentLocked && (
                    <Badge variant="destructive" className="flex items-center gap-1 text-xs">
                      <LockKeyhole className="h-3 w-3" />
                      PERMANENT LOCK
                    </Badge>
                  )}
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground truncate">{setting.bots?.type}</p>
              </div>
              <div className="flex flex-col gap-2">
                {/* Main Enable/Disable */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Enabled</span>
                  <Switch
                    checked={setting.enabled}
                    onCheckedChange={(enabled) => onUpdate(setting.id, { enabled })}
                    disabled={isUpdating}
                  />
                </div>
                
                {/* Forward Enable/Disable */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Forward</span>
                  <Switch
                    checked={setting.forward_enabled}
                    onCheckedChange={(forward_enabled) => 
                      onUpdate(setting.id, { forward_enabled })
                    }
                    disabled={isUpdating || !setting.enabled}
                  />
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t" />

            {/* Permanent Lock Warning */}
            {isPermanentLocked && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <div className="flex items-start gap-2">
                  <LockKeyhole className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                  <div className="flex-1 space-y-1">
                    <p className="text-sm font-medium text-destructive">
                      Permanently Locked
                    </p>
                    <p className="text-xs text-destructive/80">
                      Pattern: <code className="font-mono bg-destructive/20 px-1 rounded">{patternInfo?.lock_pattern}</code>
                    </p>
                    <p className="text-xs text-destructive/70">
                      All signals are being skipped until admin manually unlocks.
                    </p>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        className="gap-1 flex-shrink-0"
                      >
                        <LockKeyhole className="h-3 w-3" />
                        <span className="hidden sm:inline">Unlock</span>
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Unlock Permanent Lock?</AlertDialogTitle>
                        <AlertDialogDescription className="space-y-2">
                          <p>
                            Bot <strong>{setting.bots?.name}</strong> is locked by permanent pattern:
                          </p>
                          <code className="block p-2 bg-muted rounded font-mono text-sm">
                            "{patternInfo?.lock_pattern}"
                          </code>
                          <p className="text-destructive font-medium">
                            ⚠️ Warning: If this pattern matches again, the bot will be locked again.
                          </p>
                          <p>
                            Continue with manual unlock?
                          </p>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => unlockMutation.mutate()}
                          disabled={unlockMutation.isPending}
                          className="bg-destructive hover:bg-destructive/90"
                        >
                          {unlockMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                          Confirm Unlock
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            )}

            {/* Current State */}
            <div className="space-y-2 text-xs sm:text-sm">
              <div>
                <span className="text-muted-foreground">Current:</span>
                <Badge variant={getModeColor()} className="ml-2 text-xs">
                  {state?.mode || 'GATE'}
                </Badge>
                {isReverseMode && (
                  <Badge variant="outline" className="ml-1 text-[10px] px-1">
                    🔄 REVERSE
                  </Badge>
                )}
              </div>
              
              <div className="flex items-center justify-between gap-2">
                <div>
                  <span className="text-muted-foreground">Recent:</span>
                  <span className="ml-2 font-mono text-xs break-all">
                    {state?.recent_results ? state.recent_results.slice(-10) : '(empty)'}
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsEditPatternOpen(true)}
                  className="h-6 px-2 text-xs"
                >
                  <Edit className="h-3 w-3 mr-1" />
                  Edit
                </Button>
              </div>
              
              <div>
                <span className="text-muted-foreground">Rules:</span>
                <Badge variant="outline" className="ml-2 text-xs">
                  {patternCount} pattern{patternCount !== 1 ? 's' : ''}
                </Badge>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t" />

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="default"
                onClick={() => setIsPatternsOpen(true)}
                className="flex-1 px-2 sm:px-3"
              >
                <Settings className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
                <span className="hidden sm:inline">Patterns</span>
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onReset(setting.bot_id)}
                disabled={isResetting}
                className="px-2 sm:px-3"
              >
                <RotateCcw className="h-3 w-3 sm:h-4 sm:w-4" />
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setIsDeleteOpen(true)}
                disabled={isDeleting}
                className="px-2 sm:px-3"
              >
                <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Type2PatternsDialog
        open={isPatternsOpen}
        onOpenChange={setIsPatternsOpen}
        setting={setting}
        onUpdate={onUpdate}
        isUpdating={isUpdating}
      />

      <DeleteConfirmDialog
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        botName={setting.bots?.name || 'Unknown Bot'}
        skipType="Type 2"
        onConfirm={() => {
          onDelete(setting.id);
          setIsDeleteOpen(false);
        }}
        isDeleting={isDeleting}
      />

      <EditRecentPatternDialog
        open={isEditPatternOpen}
        onOpenChange={setIsEditPatternOpen}
        botId={setting.bot_id}
        botName={setting.bots?.name || 'Unknown Bot'}
        currentPattern={state?.recent_results || ''}
        currentMode={state?.mode || 'GATE'}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['type2-bot-states'] })}
      />
    </>
  );
};
